import { Box2, Vector2, Vector2Tuple, Vector3 } from 'three';

import { getOrientedBox2Corners } from '@spazious/dollhouse-engine';

import { FEnsembleBase } from '../fensembles/index.js';
import {
  Schema,
  SchemaFieldBoolean,
  SchemaFieldEnum,
  SchemaFieldNumber,
  SchemaFieldTypesMapping,
  SchemaValues,
} from '../schema/index.js';
import { FInstanceEnsembleNode, FInstanceNode } from '../types/index.js';
import { truncateDecimals } from '../utils/mathHelpers.js';

import {
  FArrangementBase,
  FArrangementBaseSchema,
  FArrangementBaseSchemaFields,
} from './FArrangementBase.js';

const TAU = Math.PI * 2;
const vec2 = new Vector2();
const vec3 = new Vector3();

export interface FArrangementGridSchemaFields extends FArrangementBaseSchemaFields {
  fill: SchemaFieldBoolean;
  direction: SchemaFieldEnum<string>;
  elements: SchemaFieldNumber;
  elementRotation: SchemaFieldNumber;
  columns: SchemaFieldNumber;
  columnsDistance: SchemaFieldNumber;
  rows: SchemaFieldNumber;
  rowsDistance: SchemaFieldNumber;
  displacement: SchemaFieldNumber;
  aisles: SchemaFieldNumber;
  aislesWidth: SchemaFieldNumber;
  aislesDirection: SchemaFieldEnum<string>;
  crisscross: SchemaFieldBoolean;
}

export interface FArrangementGridSchema extends FArrangementBaseSchema {
  fields: FArrangementGridSchemaFields;
}

export interface FArrangementGridValues {
  elementSize: SchemaFieldTypesMapping['vec2'];
  maxElements: SchemaFieldTypesMapping['number'];
}

export type FArrangementGridAttributes = Partial<SchemaValues<FArrangementGridSchemaFields>> &
  FArrangementGridValues;

export abstract class FArrangementGrid<
  TSchema extends FArrangementGridSchema = FArrangementGridSchema,
  TValues extends Record<string, any> = {},
  TFEnsembleSchema extends Schema = Schema,
  TFEnsembleValues extends Record<string, any> = {}
> extends FArrangementBase<
  TSchema,
  TValues & FArrangementGridValues,
  TFEnsembleSchema,
  TFEnsembleValues
> {
  usesEnsembles: boolean = false;
  ensembles: FInstanceEnsembleNode[] = [];

  constructor(
    schema: TSchema,
    initialValues?: FArrangementGridAttributes & TValues & TFEnsembleValues,
    fcomposition?: FEnsembleBase<TFEnsembleSchema, TFEnsembleValues>
  ) {
    super(
      schema,
      {
        maxElements: Infinity,
        elementSize: [0, 0],
        ...(initialValues as any),
      },
      fcomposition
    );
    this.setValue('getMaxRowDistance', this.getMaxRowDistance);
    this.setValue('getMaxColumnDistance', this.getMaxColumnDistance);

    this.setValue('getMaxAislesCount', this.getMaxAislesCount);
    this.setValue('getMaxAisleWidth', this.getMaxAisleWidth);
    this.setValue('getMin', this.getMin);
  }

  /**
   * Calculate number of grid elements as per current value of
   * - number of aisles and aislesWidth
   * - column and row distances
   * - elementSize
   */
  getMaxGridElements = () => {
    const {
      size,
      columnsDistance,
      rowsDistance,
      aisles,
      aislesWidth,
      aislesDirection,
      crisscross,
      direction,
    } = this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const [elementSizeX, elementSizeY] = this.getElementSize();
    const [sizeX, sizeY] = size;
    const iXGap = columnsDistance + elementSizeX;
    const iYGap = rowsDistance + elementSizeY;

    let columnAisles = aislesDirection === 'column' ? aisles : 0;
    let rowAisles = aislesDirection === 'row' ? aisles : 0;

    const groupSizeX = (sizeX - columnAisles * aislesWidth) / (columnAisles + 1);
    const columnsPerGroup =
      groupSizeX >= elementSizeX ? 1 + Math.floor((groupSizeX - elementSizeX) / iXGap) : 0;
    const columns = columnsPerGroup * (columnAisles + 1);

    const groupSizeY = (sizeY - rowAisles * aislesWidth) / (rowAisles + 1);
    const rowsPerGroup =
      groupSizeY >= elementSizeY ? 1 + Math.floor((groupSizeY - elementSizeY) / iYGap) : 0;
    const rows = rowsPerGroup * (rowAisles + 1);

    if (crisscross) {
      const dirRow = /^row/.test(direction);
      if (dirRow) {
        // alternate rows have one element less
        return rows * columns - Math.floor(rows / 2) * 1;
      } else {
        // alternate columns have one element less
        return rows * columns - Math.floor(columns / 2) * 1;
      }
    }

    return columns * rows;
  };

  /**
   * Calculate max row distance based on current aisles count and aisles width
   * such that at least two rows of furnitures are present in each group
   */
  getMaxRowDistance = () => {
    const { size, aislesWidth, aisles, aislesDirection } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const [, elementSizeY] = this.getElementSize();
    const [, sizeY] = size;
    if (aisles === 0 || aislesDirection !== 'row') {
      return Math.max(0, sizeY - elementSizeY * 2);
    }

    const groupSizeY = (sizeY - aisles * aislesWidth) / (aisles + 1);

    return Math.max(0, groupSizeY - 2 * elementSizeY);
  };

  /**
   * Calculate max column distance based on current aisles count and aisles width
   * such that at least two columns of furnitures are present in each group
   */
  getMaxColumnDistance = () => {
    const { size, aislesWidth, aisles, aislesDirection } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const [elementSizeX] = this.getElementSize();
    const [sizeX] = size;
    if (aisles === 0 || aislesDirection !== 'column') {
      return Math.max(0, sizeX - elementSizeX * 2);
    }

    const groupSizeX = (sizeX - aisles * aislesWidth) / (aisles + 1);

    return Math.max(0, groupSizeX - 2 * elementSizeX);
  };

  getMaxAislesCount = () => {
    const { size, aislesWidth, aislesDirection } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;
    const elementSize = this.getElementSize();
    const [sizeX, sizeY] = size;
    let maxAisles;
    if (aislesWidth === 0) {
      maxAisles = 0;
    } else {
      if (aislesDirection === 'column') {
        maxAisles = Math.floor((sizeX - elementSize[0]) / (aislesWidth + elementSize[0]));
      } else {
        maxAisles = Math.floor((sizeY - elementSize[1]) / (aislesWidth + elementSize[1]));
      }
    }
    return maxAisles;
  };

  getMaxAisleWidth = () => {
    const { size, aislesDirection, aisles, columnsDistance, rowsDistance } =
      this.getValues() as SchemaValues<FArrangementGridSchemaFields> & FArrangementGridValues;

    const elementSize = this.getElementSize();
    const [sizeX, sizeY] = size;
    let width;
    if (aisles === 0) {
      if (aislesDirection === 'column') {
        width = Math.max(0, sizeX - 2 * elementSize[0] - columnsDistance);
      } else {
        width = Math.max(0, sizeY - 2 * elementSize[1] - rowsDistance);
      }
    } else {
      if (aislesDirection === 'column') {
        width = Math.max(0, (sizeX - (aisles + 1) * elementSize[0]) / aisles);
      } else {
        width = Math.max(0, (sizeY - (aisles + 1) * elementSize[1]) / aisles);
      }
    }

    return truncateDecimals(width, 2);
  };

  getMin = (...args: number[]) => {
    return Math.min(...args);
  };

  updateObjectMeasurements(): void {
    const bbox = this.getInstanceBoundingBox();

    bbox.getSize(vec3);
    const elementSize = new Vector2(vec3.x, vec3.z);

    // TODO fix typings
    this.updateValues({
      elementSize: elementSize.toArray(),
    } as any);
  }

  getMinimumSize() {
    const bbox = this.getInstanceBoundingBox();
    bbox.getSize(vec3);
    const minimumSize = new Vector2(vec3.x, vec3.z);
    return minimumSize.toArray();
  }

  getMaxIterations(maxColumns: number, maxRows: number, ignoreMaxElements: boolean = false) {
    const { elements } = this.getValue('elements');
    return this.getSafeIterations(
      Math.min(ignoreMaxElements ? Infinity : elements, maxColumns * maxRows)
    );
  }

  getNodeAttrs(
    // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
    idx: number
  ): SchemaValues<TFEnsembleSchema['fields'] & TFEnsembleValues> {
    return this.getValues();
  }

  protected getCompositionNodes(
    /* eslint-disable unused-imports/no-unused-vars, no-unused-vars */
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
    /* eslint-enable unused-imports/no-unused-vars, no-unused-vars */
  ): FInstanceNode[] {
    throw new Error('FArrangementGrid.getCompositionNodes Method not implemented.');
  }

  getElementSize() {
    return this.getValue('elementSize');
  }

  computeNodes(ignoreMaxElements: boolean = false): FInstanceNode[] {
    if (this.getObjects().length === 0) {
      return [];
    }

    const values = this.getValues() as SchemaValues<FArrangementGridSchemaFields> &
      FArrangementGridValues;
    const {
      orientation,
      mask,
      size,
      columnsDistance,
      rowsDistance,
      direction,
      aisles,
      aislesWidth,
      aislesDirection,
      crisscross,
    } = values;

    const [sizeX, sizeY] = size;

    const elementSize = this.getElementSize();

    const iXGap = columnsDistance + elementSize[0];
    const iYGap = rowsDistance + elementSize[1];

    let groupSizeX = sizeX;
    let groupSizeY = sizeY;
    let rowGroups = 1,
      columnGroups = 1;
    if (aisles > 0) {
      if (aislesDirection === 'column') {
        groupSizeX = (sizeX - aisles * aislesWidth) / (aisles + 1);
        columnGroups = aisles + 1;
      } else {
        groupSizeY = (sizeY - aisles * aislesWidth) / (aisles + 1);
        rowGroups = aisles + 1;
      }
    }
    const columnsPerGroup =
      groupSizeX >= elementSize[0] ? 1 + Math.floor((groupSizeX - elementSize[0]) / iXGap) : 0;
    const rowsPerGroup =
      groupSizeY >= elementSize[1] ? 1 + Math.floor((groupSizeY - elementSize[1]) / iYGap) : 0;

    let columns = columnsPerGroup * columnGroups;
    let rows = rowsPerGroup * rowGroups;

    const dirRow = /^row/.test(direction);
    const dirReverse = /-reverse$/.test(direction);

    const maxElements = this.getMaxIterations(columns, rows, ignoreMaxElements);

    const theta = orientation * TAU;
    const pivot = mask?.length ? this.getMaskPivot(mask) : new Vector2(0, 0);

    const [eHalfSizeX, eHalfSizeY] = [elementSize[0] * 0.5, elementSize[1] * 0.5];
    const bounds = {
      minX: -sizeX / 2 + eHalfSizeX,
      minY: -sizeY / 2 + eHalfSizeY,
      maxX: sizeX / 2 - eHalfSizeX,
      maxY: sizeY / 2 - eHalfSizeY,
    };

    let gridElements = 0;

    const cursor = new Vector2(bounds.minX, bounds.minY);

    // outputs
    const nodes: FInstanceNode[] = [];
    const ensembles: FInstanceEnsembleNode[] = [];

    // Divide bounding box into (aisles + 1) groups along aislesDirection, with aislesWidth gap between
    let groups: Box2[] = [];
    if (aisles > 0) {
      const isCol = aislesDirection === 'column';
      const total = aisles + 1;
      const [sizeX, sizeY] = size;
      let startX = -sizeX / 2,
        startY = -sizeY / 2;
      if (isCol) {
        const groupW = (sizeX - aisles * aislesWidth) / total;
        for (let i = 0; i < total; i++) {
          const minX = startX + i * (groupW + aislesWidth);
          const maxX = minX + groupW;
          groups.push(
            new Box2(
              new Vector2(minX + eHalfSizeX, startY + eHalfSizeY),
              new Vector2(maxX - eHalfSizeX, startY + sizeY - eHalfSizeY)
            )
          );
        }
      } else {
        const groupH = (sizeY - aisles * aislesWidth) / total;
        for (let i = 0; i < total; i++) {
          const minY = startY + i * (groupH + aislesWidth);
          const maxY = minY + groupH;
          groups.push(
            new Box2(
              new Vector2(startX + eHalfSizeX, minY + eHalfSizeY),
              new Vector2(startX + sizeX - eHalfSizeX, maxY - eHalfSizeY)
            )
          );
        }
      }
    } else {
      // single group: whole box
      groups = [
        new Box2(
          new Vector2(-size[0] / 2 + eHalfSizeX, -size[1] / 2 + eHalfSizeY),
          new Vector2(size[0] / 2 - eHalfSizeX, size[1] / 2 - eHalfSizeY)
        ),
      ];
    }

    const placeElement = (cursor: Vector2) => {
      const position = vec2
        .set(cursor.x, cursor.y)
        .rotateAround(new Vector2(0, 0), theta)
        .add(pivot)
        .toArray();
      let count = 0;
      let { newNodes, isWithinMask } = this.createNodesWithinMask(
        mask,
        position,
        elementSize,
        theta,
        gridElements
      );
      if (isWithinMask && newNodes.length > 0) {
        nodes.push(...newNodes);
        ensembles.push(...this.getEnsembles({ position, row: 0, column: 0 }, gridElements));
        gridElements++;
        count++;
      }
      return count;
    };

    if (dirRow && aislesDirection === 'column') {
      let sortedGroups = [...groups];
      cursor.y = dirReverse ? sortedGroups[0].max.y : sortedGroups[0].min.y;
      let row = 0;
      const offsetX = crisscross ? iXGap : 0.1; // use only slots if crisscross
      while (gridElements < maxElements && cursor.y <= bounds.maxY && cursor.y >= bounds.minY) {
        let elementsInRow = 0;
        for (let i = 0; i < sortedGroups.length; i++) {
          const group = sortedGroups[i];
          if (i === 0 && sortedGroups.length > 1) {
            // layout from right to left
            cursor.x = group.max.x - (crisscross && row % 2 === 1 ? iXGap / 2 : 0);
            while (cursor.x >= group.min.x && gridElements < maxElements) {
              if (placeElement(cursor)) {
                elementsInRow++;
                cursor.x -= iXGap;
              } else {
                cursor.x -= offsetX; // use only slots if crisscross
              }
            }
          } else {
            // layout from left to right
            cursor.x = group.min.x + (crisscross && row % 2 === 1 ? iXGap / 2 : 0);
            while (cursor.x <= group.max.x && gridElements < maxElements) {
              if (placeElement(cursor)) {
                elementsInRow++;
                cursor.x += iXGap;
              } else {
                cursor.x += offsetX;
              }
            }
          }
        }
        if (elementsInRow === 0) {
          cursor.y += dirReverse ? -0.1 : 0.1; // push cursor marginally
        } else {
          cursor.y += dirReverse ? -iYGap : iYGap; // move cursor to next row
          row++;
        }
      }
    } else if (dirRow && aislesDirection === 'row') {
      // from front group to back group
      // in each group, place elements row by row
      let sortedGroups = dirReverse ? [...groups].reverse() : [...groups];
      let row = 0;
      const offsetX = crisscross ? iXGap : 0.1; // use only slots if crisscross
      for (let i = 0; i < sortedGroups.length; i++) {
        const group = sortedGroups[i];
        cursor.y = dirReverse ? group.max.y : group.min.y;
        while (cursor.y <= group.max.y && cursor.y >= group.min.y && gridElements < maxElements) {
          let elementsInRow = 0;
          cursor.x = group.min.x + (crisscross && row % 2 === 1 ? iXGap / 2 : 0);
          while (cursor.x <= group.max.x && gridElements < maxElements) {
            if (placeElement(cursor)) {
              elementsInRow++;
              cursor.x += iXGap;
            } else {
              cursor.x += offsetX;
            }
          }
          if (elementsInRow === 0) {
            cursor.y += dirReverse ? -0.1 : 0.1; // push cursor marginally
          } else {
            cursor.y += dirReverse ? -iYGap : iYGap; // move cursor to next row
            row++;
          }
        }
        if (gridElements >= maxElements) {
          break;
        }
      }
    } else if (!dirRow && aislesDirection === 'column') {
      // from left group to right group
      // in each group, place elements column by column
      let sortedGroups = dirReverse ? [...groups].reverse() : [...groups];
      let column = 0;
      const offsetY = crisscross ? iYGap : 0.1; // use only slots if crisscross
      for (let i = 0; i < sortedGroups.length; i++) {
        const group = sortedGroups[i];
        cursor.x = dirReverse ? group.max.x : group.min.x;
        while (cursor.x <= group.max.x && cursor.x >= group.min.x && gridElements < maxElements) {
          let elementsInColumn = 0;
          cursor.y = group.min.y + (crisscross && column % 2 === 1 ? iYGap / 2 : 0);
          while (cursor.y <= group.max.y && gridElements < maxElements) {
            if (placeElement(cursor)) {
              elementsInColumn++;
              cursor.y += iYGap;
            } else {
              cursor.y += offsetY;
            }
          }
          if (elementsInColumn === 0) {
            cursor.x += dirReverse ? -0.1 : 0.1; // push cursor marginally
          } else {
            cursor.x += dirReverse ? -iXGap : iXGap; // move cursor to next row
            column++;
          }
        }
        if (gridElements >= maxElements) {
          break;
        }
      }
    } else if (!dirRow && aislesDirection === 'row') {
      let sortedGroups = [...groups];
      cursor.x = dirReverse ? sortedGroups[0].max.x : sortedGroups[0].min.x;
      let column = 0;
      const offsetY = crisscross ? iYGap : 0.1; // use only slots if crisscross
      while (gridElements < maxElements && cursor.x <= bounds.maxX && cursor.x >= bounds.minX) {
        let elementsInColumn = 0;
        for (let i = 0; i < sortedGroups.length; i++) {
          const group = sortedGroups[i];
          if (i === 0 && sortedGroups.length > 1) {
            // layout from aisles to away from aisles
            cursor.y = group.max.y - (crisscross && column % 2 === 1 ? iYGap / 2 : 0);
            while (cursor.y >= group.min.y && gridElements < maxElements) {
              if (placeElement(cursor)) {
                elementsInColumn++;
                cursor.y -= iYGap;
              } else {
                cursor.y -= offsetY;
              }
            }
          } else {
            // layout from front to back
            cursor.y = group.min.y + (crisscross && column % 2 === 1 ? iYGap / 2 : 0);
            while (cursor.y <= group.max.y && gridElements < maxElements) {
              if (placeElement(cursor)) {
                elementsInColumn++;
                cursor.y += iYGap;
              } else {
                cursor.y += offsetY;
              }
            }
          }
        }
        if (elementsInColumn === 0) {
          cursor.x += dirReverse ? -0.1 : 0.1; // push cursor marginally
        } else {
          cursor.x += dirReverse ? -iXGap : iXGap; // move cursor to next row
          column++;
        }
      }
    }

    this.ensembles = ensembles;

    return nodes as [FInstanceNode];
  }

  createNodesWithinMask(
    mask: Vector2Tuple[][],
    position: Vector2Tuple,
    elementSize: Vector2Tuple,
    theta: number,
    gridElements: number
  ) {
    // assume nodes will be within bounds by default
    // check whole element bounding box against the outlines of the fensemble(mask) if present
    // then calculate actual nodes and check against the navmesh
    let isWithinMask = true;
    if (mask?.length) {
      const boxCorners = getOrientedBox2Corners(position, elementSize, theta);

      isWithinMask = this.polygonContains(mask, [boxCorners]);
    }

    let newNodes = this.getCompositionNodes({ position, row: 0, column: 0 }, gridElements);
    if (isWithinMask) {
      // check against navmesh
      newNodes = newNodes.filter((node) => this.isNodeWithinNavmesh(node));
      // ensure there are at least one node of each object type
      const objectTypes = new Set(newNodes.map((node) => node.id));
      if (objectTypes.size < this.getObjects().length) {
        isWithinMask = false;
      }
    }

    return { newNodes, isWithinMask };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getEnsembles(
    // eslint-disable-next-line no-unused-vars
    _params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    // eslint-disable-next-line no-unused-vars
    _idx: number
  ): FInstanceEnsembleNode[] {
    return [];
  }
}
